// VOY-485 (rebuild) — Currency & locale (Settings tab).
//
// Currency / language / timezone / date format + "Show prices in
// destination currency" toggle. Reads + writes via getPreferences /
// savePreferences with preference_type='locale' (new slice, separate from
// 'travel' which is the categorical preferences). Defaults from Intl
// where possible (timezone, language).

import { useEffect, useState, type ChangeEvent } from 'react'
import { getPreferences, savePreferences } from '@/lib/profile'

export interface CurrencyLocaleSectionProps {
  userId: string
}

interface LocalePrefs {
  currency?: string
  language?: string
  timezone?: string
  date_format?: string
  show_destination_currency?: boolean
}

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'BRL', 'ARS', 'MXN']
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'pt', label: 'Português' },
]
const DATE_FORMATS = ['DD / MM / YYYY', 'MM / DD / YYYY', 'YYYY-MM-DD']

function defaultTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return 'UTC' }
}

export function CurrencyLocaleSection({ userId }: CurrencyLocaleSectionProps) {
  const [prefs, setPrefs] = useState<LocalePrefs>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await getPreferences(userId, 'locale')
        if (!cancelled) setPrefs((data as LocalePrefs) ?? {})
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId])

  async function patch(next: Partial<LocalePrefs>) {
    setSaving(true)
    const merged = { ...prefs, ...next }
    setPrefs(merged)
    try {
      await savePreferences(userId, 'locale', merged as Record<string, unknown>)
    } catch (err) {
      console.error('[CurrencyLocaleSection] save failed:', err)
    } finally {
      setSaving(false)
    }
  }

  const currency = prefs.currency ?? 'USD'
  const language = prefs.language ?? 'en'
  const timezone = prefs.timezone ?? defaultTimezone()
  const dateFormat = prefs.date_format ?? 'DD / MM / YYYY'

  return (
    <section className="card" aria-labelledby="locale-h">
      <div className="card-h">
        <h3 id="locale-h">Currency &amp; locale</h3>
        {saving && <span className="meta">SAVING…</span>}
      </div>
      <div className="stack">
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-l">CURRENCY</span>
            <select value={currency} disabled={loading} onChange={(e: ChangeEvent<HTMLSelectElement>) => void patch({ currency: e.target.value })}>
              {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-l">LANGUAGE</span>
            <select value={language} disabled={loading} onChange={(e: ChangeEvent<HTMLSelectElement>) => void patch({ language: e.target.value })}>
              {LANGUAGE_OPTIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </label>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-l">TIMEZONE</span>
            <input
              type="text"
              value={timezone}
              disabled={loading}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPrefs((p) => ({ ...p, timezone: e.target.value }))}
              onBlur={() => void patch({ timezone })}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-l">DATE FORMAT</span>
            <select value={dateFormat} disabled={loading} onChange={(e: ChangeEvent<HTMLSelectElement>) => void patch({ date_format: e.target.value })}>
              {DATE_FORMATS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </div>
        <div className="toggle-row">
          <div>
            <div className="toggle-row-label">Show prices in destination currency</div>
            <div className="toggle-row-hint">Useful when comparing local hotel rates</div>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={Boolean(prefs.show_destination_currency)}
              disabled={loading}
              onChange={(e: ChangeEvent<HTMLInputElement>) => void patch({ show_destination_currency: e.target.checked })}
              aria-label="Show prices in destination currency"
            />
            <span className="track" />
          </label>
        </div>
      </div>
    </section>
  )
}
